import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import styles from 'styles/components/template.module.scss'
import text from 'styles/utility/text.module.scss'
import { Spacer } from 'components/Utility'
import { useDispatch, useSelector } from 'react-redux'
import { fetchOrdersInPeriod, initOrders, unsubscribeOrders } from 'reducks/checkout/operations'
import { State } from 'index'
import { getOrders } from 'reducks/checkout/selectors'
import { RevenueReportCard, RevenueTotalCardList } from 'components/Card'
import { FlexRow } from 'components/Flex'
import { ToggleSquareButton } from 'components/Form'
import { RevenueCalendarContainer } from 'components/Calendar'
import { getHolidays } from 'reducks/store/selectors'
import { addMonths, endOfMonth, isAfter, isBefore, isSameDay, startOfMonth } from 'date-fns'
import { fetchClosedDates } from 'reducks/calendar/operations'
import { OrderType } from 'types/checkout'
import { parseToDate } from 'lib/helper'
import { getStoreRef } from 'reducks/auth/selectors'
import { NavButton } from 'components/Button'
import { getSpecialHolidays } from 'reducks/calendar/selectors'
import { fetchBreads } from 'reducks/bread/operations'

const RealtimeRevenueTemplate: FC = () => {
  const dispatch = useDispatch()

  const orders = useSelector((state: State) => getOrders(state))
  const holidays = useSelector((state: State) => getHolidays(state))
  const specialHolidays = useSelector((state: State) => getSpecialHolidays(state))
  const storeRef = useSelector((state: State) => getStoreRef(state))

  const [accountingType, setAccountingType] = useState<'order' | 'deliver'>('order')
  const [startDate, setStartDate] = useState<Date>(new Date())
  const [endDate, setEndDate] = useState<Date>(new Date())
  const [orderList, setOrderList] = useState<OrderType[]>([])
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date())

  const selectCurrentMonth = useCallback((date: Date) => setCurrentMonth(date), [setCurrentMonth])

  // 開始日の選択→終了日の選択を行う
  const selectDate = useCallback(
    (date: Date) => {
      if (isAfter(date, endDate)) {
        setEndDate(date)
      } else {
        setStartDate(date)
        setEndDate(date)
      }
    },
    [startDate, endDate, setStartDate, setEndDate]
  )

  const toggleAccountingType = useCallback(
    (type: 'order' | 'deliver') => {
      setAccountingType(type)
    },
    [setAccountingType]
  )

  const updatePeriod = useCallback(
    (start: Date, end: Date) => {
      setStartDate(start)
      setEndDate(end)
    },
    [setStartDate, setEndDate]
  )

  const startMonth = useMemo(() => startOfMonth(addMonths(currentMonth, -1)), [currentMonth])
  const endMonth = useMemo(() => endOfMonth(currentMonth), [currentMonth])

  // パン情報を取得
  useEffect(() => {
    if (!storeRef) return
    dispatch(fetchBreads(storeRef, 'all', 'order'))
  }, [storeRef])

  // 休業日を取得
  useEffect(() => {
    if (!startMonth || !endMonth || !storeRef) return
    dispatch(fetchClosedDates(startMonth, endMonth, holidays, specialHolidays, storeRef))
  }, [startMonth, endMonth, holidays, storeRef, specialHolidays])

  // 注文を取得
  useEffect(() => {
    if (!startMonth || !endMonth || !storeRef) return
    dispatch(initOrders())
    dispatch(
      fetchOrdersInPeriod(
        startMonth,
        endMonth,
        accountingType,
        storeRef,
        ['succeeded', 'requires_capture'],
        ['daily', 'preorder', 'delivery']
      )
    )
    return () => {
      dispatch(unsubscribeOrders())
    }
  }, [startMonth, endMonth, accountingType, storeRef])

  // 反映された期間に応じてordersを絞り込む
  useEffect(() => {
    if (!startDate || !endDate) return
    if (accountingType === 'order') {
      const nextState = orders.filter((order) => {
        const orderedDate = order.checkout_at ? order.checkout_at.toDate() : order.updated_at.toDate()
        return (
          isSameDay(orderedDate, startDate) ||
          isSameDay(orderedDate, endDate) ||
          (isAfter(orderedDate, startDate) && isBefore(orderedDate, endDate))
        )
      })
      setOrderList([...nextState])
    } else {
      const nextState = orders.filter((order) => {
        const deliveredDate = parseToDate(order.date)
        return (
          isSameDay(deliveredDate, startDate) ||
          isSameDay(deliveredDate, endDate) ||
          (isAfter(deliveredDate, startDate) && isBefore(deliveredDate, endDate))
        )
      })
      setOrderList([...nextState])
    }
  }, [startDate, endDate, accountingType, orders])

  return (
    <div className={styles.standard}>
      <Spacer size={28} />
      <FlexRow justifyContent={'end'}>
        <span className={text.font_bold}>計上設定</span>
        <Spacer size={8} />
        <ToggleSquareButton type={accountingType} onClick={toggleAccountingType} />
      </FlexRow>
      <Spacer size={28} />
      <RevenueCalendarContainer
        accountingType={accountingType}
        currentMonth={currentMonth}
        startMonth={startMonth}
        endMonth={endMonth}
        startDate={startDate}
        endDate={endDate}
        selectDate={selectDate}
        selectCurrentMonth={selectCurrentMonth}
        updatePeriod={updatePeriod}
      />
      <Spacer size={28} />
      <RevenueTotalCardList orderList={orderList} />
      <Spacer size={12} />
      <RevenueReportCard accountingType={accountingType} orderList={orderList} />
      <Spacer size={48} />
      <NavButton label={'トップページに戻る'} iconPath={'/icon/grey/home.svg'} url={'/'} />
      <Spacer size={72} />
    </div>
  )
}

export default RealtimeRevenueTemplate
